import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, Calendar, Loader2, TrendingUp, TrendingDown, Minus, Truck, Package, AlertTriangle, FileText, Trash2, Users, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ----- date helpers -----
const todayIso = () => new Date().toISOString().slice(0, 10);
const ytdStartIso = () => {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
};
const addDaysIso = (iso, days) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const PRESETS = [
  { id: 'ytd', label: 'YTD', range: () => ({ from: ytdStartIso(), to: todayIso() }) },
  { id: '90d', label: 'Last 90d', range: () => ({ from: addDaysIso(todayIso(), -89), to: todayIso() }) },
  { id: '12m', label: 'Last 12 mo', range: () => ({ from: addDaysIso(todayIso(), -364), to: todayIso() }) },
  { id: 'custom', label: 'Custom', range: null },
];

const fmtEur = (n) => `€${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (n) => Number(n || 0).toLocaleString();
const fmtKg = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} kg`;
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

// Coloured delta pill
const Delta = ({ value, invert = false }) => {
  if (value == null) return <span className="text-zinc-500 text-xs">— no prior</span>;
  const positive = invert ? value < 0 : value > 0;
  const negative = invert ? value > 0 : value < 0;
  const Icon = value === 0 ? Minus : positive ? TrendingUp : TrendingDown;
  const cls = value === 0 ? 'text-zinc-500' : positive ? 'text-emerald-400' : 'text-rose-400';
  const sign = value > 0 ? '+' : '';
  return (
    <span className={`text-xs flex items-center gap-0.5 ${cls}`}>
      <Icon className="w-3 h-3" />
      {sign}{value.toFixed(1)}%
    </span>
  );
};

const KpiCard = ({ label, value, delta, sub, invert = false, testid }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex flex-col gap-1" data-testid={testid}>
    <div className="text-xs text-zinc-500 uppercase tracking-wide">{label}</div>
    <div className="text-2xl font-bold text-white">{value}</div>
    <div className="flex items-center justify-between mt-1">
      <span className="text-[10px] text-zinc-500">{sub || ''}</span>
      <Delta value={delta} invert={invert} />
    </div>
  </div>
);

const AnalyticsPage = () => {
  const [tab, setTab] = useState('overview'); // 'overview' | 'shipments' | 'customers'
  const [preset, setPreset] = useState('ytd');
  const [from, setFrom] = useState(ytdStartIso());
  const [to, setTo] = useState(todayIso());
  const [basis, setBasis] = useState('shipped');
  const [includeScrap, setIncludeScrap] = useState(true);
  const [data, setData] = useState(null);
  const [shipmentRows, setShipmentRows] = useState(null);
  const [customerRows, setCustomerRows] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null); // {id, data, loading}
  const [loading, setLoading] = useState(true);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [error, setError] = useState(null);

  // Whenever preset changes (and not custom), apply its range
  useEffect(() => {
    const p = PRESETS.find(p => p.id === preset);
    if (p && p.range) {
      const r = p.range();
      setFrom(r.from);
      setTo(r.to);
    }
  }, [preset]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ from, to, basis, include_scrap: includeScrap ? 'true' : 'false' });
      const res = await fetch(`${API_URL}/api/analytics/summary?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [from, to, basis, includeScrap]);

  // Reload whenever range or basis changes.
  useEffect(() => {
    if (from && to) load();
  }, [from, to, load]);

  // Per-shipment data — lazily fetched the first time the tab is opened
  const loadShipments = useCallback(async () => {
    setShipmentsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/analytics/shipments`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShipmentRows(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load shipment analytics');
    } finally {
      setShipmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'shipments' && shipmentRows === null) {
      loadShipments();
    }
  }, [tab, shipmentRows, loadShipments]);

  // Customer analytics — lazily fetched on first tab open, then live thereafter
  const loadCustomers = useCallback(async () => {
    setCustomersLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/analytics/customers`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCustomerRows(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load customer analytics');
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'customers' && customerRows === null) {
      loadCustomers();
    }
  }, [tab, customerRows, loadCustomers]);

  const openCustomerDetail = async (customerId) => {
    setCustomerDetail({ id: customerId, data: null, loading: true, error: null });
    try {
      const res = await fetch(`${API_URL}/api/analytics/customers/${customerId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCustomerDetail({ id: customerId, data: await res.json(), loading: false, error: null });
    } catch (e) {
      setCustomerDetail({ id: customerId, data: null, loading: false, error: e.message || 'Failed to load' });
    }
  };

  // Deep-link support: /analytics?customer=<id> opens the detail view directly
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const cid = q.get('customer');
    if (cid) {
      setTab('customers');
      openCustomerDetail(cid);
    }
  }, []);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.monthly.map(m => ({
      ...m,
      monthLabel: m.month.slice(2).replace('-', '/'),  // YY/MM
    }));
  }, [data]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="analytics-page">
      <div className="flex items-center gap-3 mb-4">
        <BarChart3 className="w-6 h-6 text-emerald-400" />
        <h1 className="text-xl sm:text-2xl font-bold text-white">Analytics</h1>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 mb-4 border-b border-zinc-800">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'shipments', label: 'By Shipment', icon: Truck },
          { id: 'customers', label: 'By Customer', icon: Users },
        ].map(({ id, label, icon: Icon }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              onClick={() => { setTab(id); setCustomerDetail(null); }}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                  ? 'border-emerald-400 text-white'
                  : 'border-transparent text-zinc-500 hover:text-zinc-300'
              }`}
              data-testid={`analytics-tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && (
        <OverviewTab
          preset={preset} setPreset={setPreset}
          from={from} setFrom={setFrom}
          to={to} setTo={setTo}
          basis={basis} setBasis={setBasis}
          includeScrap={includeScrap} setIncludeScrap={setIncludeScrap}
          data={data} loading={loading} error={error}
          chartData={chartData}
          onOpenAr={() => setTab('customers')}
        />
      )}
      {tab === 'shipments' && (
        <ShipmentsTab rows={shipmentRows} loading={shipmentsLoading} error={error} apiUrl={API_URL} onReload={loadShipments} />
      )}
      {tab === 'customers' && (
        <CustomersTab
          rows={customerRows}
          loading={customersLoading}
          error={error}
          onReload={loadCustomers}
          detail={customerDetail}
          onOpenDetail={openCustomerDetail}
          onCloseDetail={() => setCustomerDetail(null)}
        />
      )}
    </div>
  );
};

// ============================================================================
// Sub-tab: Overview (KPIs + monthly chart)
// ============================================================================
const OverviewTab = ({ preset, setPreset, from, setFrom, to, setTo, basis, setBasis, includeScrap, setIncludeScrap, data, loading, error, chartData, onOpenAr }) => {
  return (
    <>
      {/* AR banner — always visible, all-time, independent of date range */}
      {data && !loading && (
        <button
          type="button"
          onClick={onOpenAr}
          className={`w-full mb-4 rounded-lg border p-4 flex items-center justify-between text-left transition-colors ${
            data.kpis.totalOutstanding > 0
              ? 'bg-rose-950/40 border-rose-900/60 hover:bg-rose-900/40'
              : 'bg-emerald-950/30 border-emerald-900/50 hover:bg-emerald-900/40'
          }`}
          data-testid="ar-banner"
        >
          <div>
            <div className="text-xs uppercase tracking-wide text-zinc-400">Total outstanding (all customers)</div>
            <div className={`text-3xl font-bold ${data.kpis.totalOutstanding > 0 ? 'text-rose-300' : 'text-emerald-300'}`}>
              {fmtEur(data.kpis.totalOutstanding)}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              {data.kpis.outstandingInvoiceCount} unpaid invoice{data.kpis.outstandingInvoiceCount === 1 ? '' : 's'} · click to see who owes what
            </div>
          </div>
          <div className="text-zinc-500 text-xs">→ By Customer</div>
        </button>
      )}
      {/* Controls */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 mb-6 flex flex-wrap items-end gap-3" data-testid="analytics-controls">
        <div className="flex gap-1">
          {PRESETS.map(p => (
            <button
              key={p.id}
              onClick={() => setPreset(p.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                preset === p.id
                  ? 'bg-emerald-600 text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200'
              }`}
              data-testid={`preset-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <Label className="text-xs text-zinc-500">From</Label>
            <Input
              type="date"
              value={from}
              onChange={(e) => { setPreset('custom'); setFrom(e.target.value); }}
              className="bg-zinc-800 border-zinc-700 text-zinc-200 h-8 text-sm"
              data-testid="date-from"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">To</Label>
            <Input
              type="date"
              value={to}
              onChange={(e) => { setPreset('custom'); setTo(e.target.value); }}
              className="bg-zinc-800 border-zinc-700 text-zinc-200 h-8 text-sm"
              data-testid="date-to"
            />
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => setIncludeScrap(!includeScrap)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              includeScrap
                ? 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200'
                : 'bg-rose-600/20 text-rose-400'
            }`}
            title="Include or completely discard coil scrap costs from all numbers"
            data-testid="analytics-scrap-toggle"
          >
            {includeScrap ? 'Scrap included' : 'Scrap excluded'}
          </button>
          <span className="w-px h-4 bg-zinc-800 mx-1" />
          {[
            { id: 'shipped', label: 'Shipped basis' },
            { id: 'paid', label: 'Paid basis' },
          ].map(b => (
            <button
              key={b.id}
              onClick={() => setBasis(b.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                basis === b.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200'
              }`}
              data-testid={`basis-${b.id}`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20" data-testid="analytics-loading">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
      )}

      {error && (
        <div className="bg-rose-950/40 border border-rose-900/50 rounded-lg p-3 text-sm text-rose-300" data-testid="analytics-error">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          {/* KPI strip — Revenue, COGS, Scrap, Overhead, Net Profit, Margin */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="analytics-kpis">
            <KpiCard
              label="Revenue"
              value={fmtEur(data.kpis.revenue)}
              delta={data.deltas.revenue}
              sub={`${fmtInt(data.kpis.invoiceCount)} invoice${data.kpis.invoiceCount === 1 ? '' : 's'}`}
              testid="kpi-revenue"
            />
            <KpiCard
              label="COGS"
              value={fmtEur(data.kpis.cogs)}
              delta={data.deltas.cogs}
              invert
              sub={`${fmtKg(data.kpis.kgSold)} sold`}
              testid="kpi-cogs"
            />
            <KpiCard
              label="Scrap"
              value={fmtEur(data.kpis.scrap)}
              delta={data.deltas.scrap}
              invert
              testid="kpi-scrap"
            />
            <KpiCard
              label="Overhead"
              value={fmtEur(data.kpis.overhead)}
              delta={data.deltas.overhead}
              invert
              sub={data.operations.monthlyOverheadEur > 0 || data.operations.perTonOverheadEur > 0 || (data.operations.overheadSchedule || []).length > 0
                ? `€${data.operations.monthlyOverheadEur}/mo (current) · €${data.operations.perTonOverheadEur}/ton`
                : 'No overhead set — adjust in Settings'}
              testid="kpi-overhead"
            />
            <KpiCard
              label="Net Profit"
              value={fmtEur(data.kpis.netProfit)}
              delta={data.deltas.netProfit}
              sub={`Gross: ${fmtEur(data.kpis.grossMargin)}`}
              testid="kpi-net-profit"
            />
            <KpiCard
              label="Margin %"
              value={fmtPct(data.kpis.marginPct)}
              delta={data.deltas.marginPct}
              sub="Net / Revenue"
              testid="kpi-margin"
            />
          </div>

          {/* Chart */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-zinc-300">Monthly performance</h2>
              <div className="text-xs text-zinc-500">
                {data.period.from} → {data.period.to} · {data.period.basis} basis
              </div>
            </div>
            <div style={{ width: '100%', height: 380 }} data-testid="analytics-chart">
              <ResponsiveContainer>
                <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="monthLabel" stroke="#71717a" fontSize={11} />
                  <YAxis stroke="#71717a" fontSize={11} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: 6, fontSize: 12 }}
                    labelStyle={{ color: '#a1a1aa' }}
                    formatter={(value, name) => [fmtEur(value), name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="revenue" name="Revenue" fill="#10b981" stackId="a" />
                  <Bar dataKey="cogs" name="COGS" fill="#ef4444" stackId="b" />
                  <Bar dataKey="scrap" name="Scrap" fill="#f59e0b" stackId="b" />
                  <Bar dataKey="overhead" name="Overhead" fill="#8b5cf6" stackId="b" />
                  <Line type="monotone" dataKey="netProfit" name="Net Profit" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 text-xs text-zinc-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Green = revenue, red/amber/violet stacked = costs (cogs + scrap + overhead), blue line = net profit.
              Prior period for deltas: {data.period.priorFrom} → {data.period.priorTo}.
            </div>
          </div>
        </>
      )}
    </>
  );
};

// ============================================================================
// Sub-tab: By Shipment (per truck profitability)
// ============================================================================
const ShipmentsTab = ({ rows, loading, error, apiUrl, onReload }) => {
  const [deleteTarget, setDeleteTarget] = useState(null);  // shipment row
  const [deleteImpact, setDeleteImpact] = useState(null);  // dry-run response
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const openDoc = (url) => {
    if (!url) return;
    let href = /^https?:\/\//i.test(url) ? url : url.startsWith('/') ? `${apiUrl}${url}` : `${apiUrl}/api/files/${url}`;
    // Carry the auth token so direct-navigation resolves to the right DB.
    const t = localStorage.getItem('auth_token');
    if (t && href.includes('/api/files/')) {
      href += (href.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(t);
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const startDelete = async (shipment) => {
    setDeleteTarget(shipment);
    setDeleteImpact(null);
    setDeleteError(null);
    try {
      const res = await fetch(`${apiUrl}/api/shipments/${shipment.shipmentId}/deletion-impact`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteImpact(await res.json());
    } catch (e) {
      setDeleteError(e.message || 'Failed to preview impact');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const res = await fetch(`${apiUrl}/api/shipments/${deleteTarget.shipmentId}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail = body.detail || body;
        const msg = typeof detail === 'string' ? detail : (detail.message || `HTTP ${res.status}`);
        throw new Error(msg);
      }
      setDeleteTarget(null);
      setDeleteImpact(null);
      onReload?.();
    } catch (e) {
      setDeleteError(e.message || 'Delete failed');
    } finally {
      setDeleteBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="shipments-loading">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-950/40 border border-rose-900/50 rounded-lg p-3 text-sm text-rose-300">
        {error}
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-12">
        <Truck className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
        <p className="text-zinc-500 text-sm">No shipments yet. Import a Purchase Order from the Stock page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="shipments-list">
      {rows.map((s) => (
        <div key={s.shipmentId} className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-testid={`shipment-row-${s.shipmentId}`}>
          {/* Header */}
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Truck className="w-4 h-4 text-purple-400" />
                <span className="text-base font-semibold text-white">
                  {s.purchaseInvoiceNumber ? `PO #${s.purchaseInvoiceNumber}` : 'Manual entry'}
                </span>
                <span className="text-sm text-zinc-500">· {s.supplier || 'Unknown'}</span>
                {s.transportPending ? (
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-amber-900/40 text-amber-300 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Transport pending
                  </span>
                ) : s.transportInvoiceNumber ? (
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-900/30 text-emerald-300">
                    Transport #{s.transportInvoiceNumber}
                  </span>
                ) : null}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {s.purchaseInvoiceDate || '—'} · {s.coilCount} coil{s.coilCount === 1 ? '' : 's'} · {fmtKg(s.originalKg)} arrived
              </div>
            </div>
            <div className="flex items-center gap-1">
              {s.purchaseInvoiceUrl && (
                <Button variant="ghost" size="sm" onClick={() => openDoc(s.purchaseInvoiceUrl)} className="text-purple-300 hover:text-purple-200 hover:bg-purple-500/10 gap-1" title="View Purchase Invoice">
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline text-xs">PO</span>
                </Button>
              )}
              {s.transportInvoiceUrl && (
                <Button variant="ghost" size="sm" onClick={() => openDoc(s.transportInvoiceUrl)} className="text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10 gap-1" title="View Transport Invoice">
                  <Truck className="w-4 h-4" />
                  <span className="hidden sm:inline text-xs">Trans</span>
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => startDelete(s)}
                className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/10 gap-1"
                title="Delete this shipment and all its data"
                data-testid={`delete-shipment-${s.shipmentId}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Body */}
          <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Column 1: Landed cost */}
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Landed cost</div>
              <div className="text-2xl font-bold text-white">{fmtEur(s.totalCost)}</div>
              <div className="text-xs text-zinc-500 space-y-0.5">
                <div>Material: {fmtEur(s.materialCost)}</div>
                <div>Transport: {s.transportPending ? <span className="text-amber-300">pending</span> : fmtEur(s.transportCost)}</div>
                <div className="text-emerald-300 font-medium pt-1">€{s.landedCostPerKg.toFixed(4)}/kg</div>
              </div>
            </div>

            {/* Column 2: Material flow */}
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Material flow</div>
              <MaterialFlowBar produced={s.producedPct} raw={s.rawPct} scrap={s.scrappedPct} />
              <div className="text-xs space-y-0.5">
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-emerald-500" /><span className="text-zinc-400">Produced:</span> <span className="text-zinc-200">{fmtKg(s.producedKg)} ({s.producedPct}%)</span></div>
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-zinc-600" /><span className="text-zinc-400">Still raw:</span> <span className="text-zinc-200">{fmtKg(s.stillRawKg)} ({s.rawPct}%)</span></div>
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-amber-500" /><span className="text-zinc-400">Scrap:</span> <span className="text-zinc-200">{fmtKg(s.scrappedKg)} ({s.scrappedPct}%) · {fmtEur(s.scrapCost)}</span></div>
              </div>
            </div>

            {/* Column 3: Sales + bottom line */}
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Sales performance</div>
              <div className="text-xs space-y-0.5">
                <div><span className="text-zinc-400">Sold:</span> <span className="text-zinc-200">{fmtInt(s.soldQty)} profiles · {fmtKg(s.soldKg)} · {s.invoiceCount} invoice{s.invoiceCount === 1 ? '' : 's'}</span></div>
                <div><span className="text-zinc-400">Revenue:</span> <span className="text-zinc-200">{fmtEur(s.soldRevenue)}</span></div>
                <div><span className="text-zinc-400">COGS:</span> <span className="text-zinc-200">{fmtEur(s.soldCogs)}</span></div>
                {s.soldMarginPct != null && (
                  <div><span className="text-zinc-400">Margin:</span> <span className={s.soldMarginPct >= 0 ? 'text-emerald-300' : 'text-rose-300'}>{fmtPct(s.soldMarginPct)}</span></div>
                )}
              </div>
              <div className="pt-2 mt-2 border-t border-zinc-800">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Bottom line so far</div>
                <div className={`text-xl font-bold ${s.realizedProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} data-testid={`realized-profit-${s.shipmentId}`}>
                  {fmtEur(s.realizedProfit)}
                </div>
                <div className="text-[10px] text-zinc-500">Realized = sales profit − scrap. Still in inventory: <span className="text-zinc-300">{fmtEur(s.unrealizedInventoryValue)}</span> at cost</div>
              </div>
            </div>
          </div>

          {/* Product breakdown */}
          {s.productBreakdown.length > 0 && (
            <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-800/20">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1">
                <Package className="w-3 h-3" /> Produced from this truck
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                {s.productBreakdown.map(p => (
                  <span key={p.productId} className="px-2 py-1 bg-zinc-800 rounded text-zinc-300">
                    {p.label}: <span className="text-zinc-100 font-medium">{p.producedProfiles}</span>
                    {p.remainingProfiles > 0 && <span className="text-zinc-500"> ({p.remainingProfiles} unsold)</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Cascade-delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteImpact(null); setDeleteError(null); } }}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg" data-testid="delete-shipment-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-rose-300 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Delete shipment?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-zinc-400 space-y-3 pt-1">
                {deleteTarget && (
                  <div className="text-zinc-300">
                    PO <span className="font-mono">#{deleteTarget.purchaseInvoiceNumber || '—'}</span> · {deleteTarget.supplier || 'Unknown'}
                  </div>
                )}

                {!deleteImpact && !deleteError && (
                  <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Checking impact…</div>
                )}

                {deleteImpact && !deleteImpact.safe && (
                  <div className="bg-rose-950/40 border border-rose-900/50 rounded p-3 text-rose-200 text-xs">
                    <div className="font-semibold mb-1">Blocked — shipped invoice(s) already consumed inventory from this shipment:</div>
                    <ul className="list-disc pl-4 space-y-0.5">
                      {deleteImpact.blockingInvoices.map(inv => (
                        <li key={inv.id}>#{inv.invoiceNumber} · {inv.customerName} · €{inv.total} · <span className="uppercase">{inv.status}</span></li>
                      ))}
                    </ul>
                    <div className="text-rose-300/80 mt-2">Revert those invoices to Draft first, then retry.</div>
                  </div>
                )}

                {deleteImpact && deleteImpact.safe && (
                  <div className="bg-zinc-800/40 border border-zinc-800 rounded p-3 text-xs space-y-1" data-testid="delete-shipment-impact">
                    <div className="text-amber-300 font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> This will permanently delete:</div>
                    <div>• {deleteImpact.summary.coils} coil{deleteImpact.summary.coils === 1 ? '' : 's'}</div>
                    <div>• {deleteImpact.summary.batches} production batch{deleteImpact.summary.batches === 1 ? '' : 'es'}</div>
                    {Object.keys(deleteImpact.summary.productStockAdjustments || {}).length > 0 && (
                      <div>• Finished stock reductions: {Object.values(deleteImpact.summary.productStockAdjustments).reduce((a, b) => a + b, 0)} profile{Object.values(deleteImpact.summary.productStockAdjustments).reduce((a, b) => a + b, 0) === 1 ? '' : 's'}</div>
                    )}
                    <div>• {deleteImpact.summary.productionLogs} production log{deleteImpact.summary.productionLogs === 1 ? '' : 's'}</div>
                    <div>• {deleteImpact.summary.wasteLogs} waste log{deleteImpact.summary.wasteLogs === 1 ? '' : 's'}</div>
                    <div>• {deleteImpact.summary.files} attached document{deleteImpact.summary.files === 1 ? '' : 's'} (archived, not bytes-deleted)</div>
                    <div className="text-zinc-500 pt-2">This cannot be undone.</div>
                  </div>
                )}

                {deleteError && (
                  <div className="bg-rose-950/40 border border-rose-900/50 rounded p-3 text-rose-200 text-xs">
                    {deleteError}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleteBusy || !deleteImpact || !deleteImpact.safe}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="delete-shipment-confirm"
            >
              {deleteBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</> : 'Delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// 3-segment proportional bar visualising kg flow: produced | raw | scrap
const MaterialFlowBar = ({ produced, raw, scrap }) => (
  <div className="w-full h-2 rounded-full overflow-hidden bg-zinc-800 flex" data-testid="material-flow-bar">
    <div style={{ width: `${produced}%` }} className="bg-emerald-500" title={`Produced: ${produced}%`} />
    <div style={{ width: `${raw}%` }} className="bg-zinc-600" title={`Still raw: ${raw}%`} />
    <div style={{ width: `${scrap}%` }} className="bg-amber-500" title={`Scrap: ${scrap}%`} />
  </div>
);

// ============================================================================
// Sub-tab: By Customer — AR + purchase behavior list, with drill-down detail
// ============================================================================
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const CustomersTab = ({ rows, loading, error, onReload, detail, onOpenDetail, onCloseDetail }) => {
  const [sortKey, setSortKey] = useState('outstanding');
  const [sortDir, setSortDir] = useState('desc');
  const [filter, setFilter] = useState('all'); // 'all' | 'owing' | 'current'
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    if (!rows) return [];
    const qNorm = q.trim().toLowerCase();
    let list = rows.filter(r => {
      if (filter === 'owing' && (r.outstanding || 0) <= 0.005) return false;
      if (filter === 'current' && (r.outstanding || 0) > 0.005) return false;
      if (qNorm) {
        const hay = `${r.customerName || ''} ${r.customerCompany || ''} ${r.customerTaxId || ''}`.toLowerCase();
        if (!hay.includes(qNorm)) return false;
      }
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;   // nulls always last
      if (bv == null) return -1;
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
    return list;
  }, [rows, sortKey, sortDir, filter, q]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'customerName' ? 'asc' : 'desc'); }
  };

  const totals = useMemo(() => {
    if (!rows) return null;
    return rows.reduce((acc, r) => {
      acc.purchased += r.totalPurchased || 0;
      acc.paid += r.totalPaid || 0;
      acc.outstanding += r.outstanding || 0;
      acc.customers += 1;
      return acc;
    }, { purchased: 0, paid: 0, outstanding: 0, customers: 0 });
  }, [rows]);

  if (detail) {
    return <CustomerDetail detail={detail} onBack={onCloseDetail} />;
  }

  return (
    <div data-testid="customers-tab">
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
      )}
      {error && !loading && (
        <div className="bg-rose-950/40 border border-rose-900/50 rounded-lg p-3 text-sm text-rose-300 flex items-center justify-between">
          <span>{error}</span>
          <Button size="sm" variant="outline" onClick={onReload} className="border-zinc-700 text-zinc-300">Retry</Button>
        </div>
      )}

      {rows && !loading && (
        <>
          {/* Summary strip */}
          {totals && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <KpiCard label="Customers" value={fmtInt(totals.customers)} sub="With activity" testid="cust-kpi-count" />
              <KpiCard label="Total purchased" value={fmtEur(totals.purchased)} sub="All-time revenue" testid="cust-kpi-purchased" />
              <KpiCard label="Total paid" value={fmtEur(totals.paid)} sub="Settled" testid="cust-kpi-paid" />
              <KpiCard label="Total outstanding" value={fmtEur(totals.outstanding)} sub="Currently owed" testid="cust-kpi-outstanding" />
            </div>
          )}

          {/* Filter controls */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 mb-4 flex flex-wrap items-end gap-3">
            <div className="flex gap-1">
              {[
                { id: 'all', label: 'All' },
                { id: 'owing', label: 'Owing me' },
                { id: 'current', label: 'Paid up' },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    filter === f.id ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200'
                  }`}
                  data-testid={`cust-filter-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="ml-auto">
              <Input
                placeholder="Search name, company, tax ID…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-200 h-8 text-sm w-64"
                data-testid="cust-search"
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-950/50">
                  <tr className="text-zinc-500 text-xs uppercase tracking-wide">
                    {[
                      ['customerName', 'Customer', 'left'],
                      ['totalPurchased', 'Purchased', 'right'],
                      ['totalPaid', 'Paid', 'right'],
                      ['outstanding', 'Outstanding', 'right'],
                      ['oldestUnpaidDays', 'Oldest unpaid', 'right'],
                      ['avgDaysLate', 'Avg days late', 'right'],
                      ['lastPurchase', 'Last purchase', 'right'],
                      ['invoiceCount', '# invoices', 'right'],
                    ].map(([k, label, align]) => (
                      <th
                        key={k}
                        onClick={() => toggleSort(k)}
                        className={`px-3 py-2 cursor-pointer select-none hover:text-zinc-200 text-${align}`}
                        data-testid={`cust-th-${k}`}
                      >
                        {label}
                        {sortKey === k && <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-8 text-zinc-500">No customers match the filter.</td></tr>
                  )}
                  {filtered.map(r => (
                    <tr
                      key={r.customerId}
                      onClick={() => onOpenDetail(r.customerId)}
                      className="border-t border-zinc-800/60 hover:bg-zinc-800/40 cursor-pointer"
                      data-testid={`cust-row-${r.customerId}`}
                    >
                      <td className="px-3 py-2.5 text-zinc-100">
                        <div className="font-medium">{r.customerName || '—'}</div>
                        {r.customerCompany && <div className="text-xs text-zinc-500">{r.customerCompany}</div>}
                      </td>
                      <td className="px-3 py-2.5 text-right text-zinc-200">{fmtEur(r.totalPurchased)}</td>
                      <td className="px-3 py-2.5 text-right text-zinc-400">{fmtEur(r.totalPaid)}</td>
                      <td className={`px-3 py-2.5 text-right font-semibold ${(r.outstanding || 0) > 0.005 ? 'text-rose-300' : 'text-emerald-300'}`}>
                        {fmtEur(r.outstanding)}
                      </td>
                      <td className="px-3 py-2.5 text-right text-zinc-300">{r.oldestUnpaidDays != null ? `${r.oldestUnpaidDays}d` : '—'}</td>
                      <td className="px-3 py-2.5 text-right text-zinc-300">{r.avgDaysLate != null ? `${r.avgDaysLate}d` : '—'}</td>
                      <td className="px-3 py-2.5 text-right text-zinc-400 text-xs">{fmtDate(r.lastPurchase)}</td>
                      <td className="px-3 py-2.5 text-right text-zinc-400">{r.invoiceCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const CustomerDetail = ({ detail, onBack }) => {
  const { data, loading, error } = detail;
  return (
    <div data-testid="customer-detail">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200"
        data-testid="cust-detail-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back to customers
      </button>

      {loading && (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      )}
      {error && (<div className="bg-rose-950/40 border border-rose-900/50 rounded-lg p-3 text-sm text-rose-300">{error}</div>)}

      {data && (
        <>
          <div className="mb-4">
            <h2 className="text-xl font-bold text-white">{data.customer?.name || '—'}</h2>
            <div className="text-sm text-zinc-500">
              {data.customer?.company && <>{data.customer.company} · </>}
              {data.customer?.taxId && <>NUI {data.customer.taxId}</>}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <KpiCard label="Purchased" value={fmtEur(data.kpis.totalPurchased)} sub={`${fmtInt(data.kpis.invoiceCount)} invoice${data.kpis.invoiceCount === 1 ? '' : 's'}`} testid="det-kpi-purchased" />
            <KpiCard label="Paid" value={fmtEur(data.kpis.totalPaid)} testid="det-kpi-paid" />
            <KpiCard label="Outstanding" value={fmtEur(data.kpis.outstanding)} testid="det-kpi-outstanding" />
            <KpiCard label="Avg days late" value={data.kpis.avgDaysLate != null ? `${data.kpis.avgDaysLate}d` : '—'} sub="Paid invoices" testid="det-kpi-days-late" />
            <KpiCard label="Oldest unpaid" value={data.kpis.oldestUnpaidDays != null ? `${data.kpis.oldestUnpaidDays}d` : '—'} testid="det-kpi-oldest" />
            <KpiCard label="Avg order size" value={fmtEur(data.kpis.avgOrderSize)} testid="det-kpi-avg-order" />
          </div>

          {/* AR balance over time */}
          {data.arSeries?.length > 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 mb-4">
              <div className="text-sm font-semibold text-zinc-300 mb-2">Outstanding balance over time</div>
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <ComposedChart data={data.arSeries} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
                    <YAxis stroke="#71717a" fontSize={11} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: 6, fontSize: 12 }}
                      formatter={(v) => [fmtEur(v), 'Balance']}
                    />
                    <Line type="stepAfter" dataKey="balance" stroke="#f43f5e" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Invoice history table */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden mb-4">
            <div className="px-4 py-2 text-sm font-semibold text-zinc-300 border-b border-zinc-800">Invoice history</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-950/50">
                  <tr className="text-zinc-500 text-xs uppercase tracking-wide">
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Shipped</th>
                    <th className="px-3 py-2 text-left">Paid</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Days to pay</th>
                    <th className="px-3 py-2 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map(r => (
                    <tr key={r.id} className="border-t border-zinc-800/60" data-testid={`inv-row-${r.id}`}>
                      <td className="px-3 py-2 text-zinc-200 font-mono">{r.invoiceNumber}</td>
                      <td className="px-3 py-2 text-zinc-400 text-xs">{fmtDate(r.shippedAt)}</td>
                      <td className="px-3 py-2 text-zinc-400 text-xs">{fmtDate(r.paidAt)}</td>
                      <td className="px-3 py-2 text-right text-zinc-200">{fmtEur(r.total)}</td>
                      <td className="px-3 py-2 text-right text-zinc-300">
                        {r.paid && r.daysToPay != null ? `${r.daysToPay}d` : (r.daysUnpaid != null ? `unpaid ${r.daysUnpaid}d` : '—')}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${r.paid ? 'bg-emerald-900/50 text-emerald-300' : 'bg-rose-900/40 text-rose-300'}`}>
                          {r.paid ? 'Paid' : 'Unpaid'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Product mix */}
          {data.productMix?.length > 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
              <div className="px-4 py-2 text-sm font-semibold text-zinc-300 border-b border-zinc-800">Top products bought (by revenue)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-950/50">
                    <tr className="text-zinc-500 text-xs uppercase tracking-wide">
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Kg</th>
                      <th className="px-3 py-2 text-right">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.productMix.map((p, i) => (
                      <tr key={p.productId || i} className="border-t border-zinc-800/60">
                        <td className="px-3 py-2 text-zinc-200">{p.productLabel || '—'}</td>
                        <td className="px-3 py-2 text-right text-zinc-300">{fmtInt(p.qty)}</td>
                        <td className="px-3 py-2 text-right text-zinc-400">{fmtKg(p.kg)}</td>
                        <td className="px-3 py-2 text-right text-zinc-200">{fmtEur(p.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AnalyticsPage;
